import logger from '@/utils/client-logger';
import { NO_DATA } from '@/utils/constants';
import { env } from '@/utils/env';
import {
  formatPrescriptionValueForDisplay,
  type PrescriptionDisplayFormatOptions,
} from '@/utils/number-format';
import { hasPrism } from '@/utils/prescription';
import {
  DeleteOutlined,
  EditOutlined,
  RightArrowOutlined,
  UploadImageOutLine,
} from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useLocale } from 'next-intl';
import { cylDiff, getADDData, moreThan5Diff, showGrayTd, sphDiff } from '../utils';
import UploadPrescriptionImg from './UploadPrescriptionImg';

export default function PrescriptionTablePC({
  prescription,
  isReadOnly = false,
  showImage = false,
  showHeaderInfo = false,
  onDelete,
  onEdit,
  onUploadImg,
  showPrism = false,
  showGray = false,
  showError = false,
}: {
  prescription: any;
  isReadOnly?: boolean;
  showImage?: boolean;
  showHeaderInfo?: boolean;
  showPrism?: boolean;
  onDelete: () => void;
  onEdit: () => void;
  onUploadImg: (path: string) => void;
  showGray?: boolean;
  showError?: boolean;
}) {
  const t = useTranslations('account.myPrescriptions');
  const locale = useLocale();
  const formatValue = (
    value: string | number | null | undefined,
    options: Omit<PrescriptionDisplayFormatOptions, 'locale'> = {},
  ) => formatPrescriptionValueForDisplay(value, { locale, ...options });
  logger.info('prescription', showImage, showHeaderInfo);
  const innerTdClassName = 'bg-white';
  const grayTdClassName = 'text-[#a9a9a9]';
  const errorTdClassName = 'text-primary-purple';
  const tableClassName = 'bg-[#f7f7f7]';

  const getPrescriptionType = (str: string, prescriptionTypeName: string) => {
    if (str === 'Sunglasses Prescription') {
      return t('prescriptionTypes.sunglasses');
    }

    return prescriptionTypeName;
  };

  return (
    <article
      className={`${isReadOnly ? '' : 'prescription-card border border-[#d4d4d4] rounded-12 p-20'}`}
    >
      {!isReadOnly && (
        <div className="flex justify-between items-start">
          <header className="flex flex-col flex-1">
            <strong className="f18 font-bs mr-10">{prescription.name}</strong>
            <time className="text-14 mt-4 font-br text-secondary-text">
              {prescription.updated_at}
            </time>
          </header>
          <span className="text-12 font-br text-primary-purple bg-[#0006cf1a] rounded-4 overflow-hidden pl-8 pr-8 py-4">
            {getPrescriptionType(
              prescription.prescription_type,
              prescription.prescription_type_name || prescription.prescription_type,
            )}
          </span>
        </div>
      )}
      <div className="prescription-table border border-[#dce1e5] rounded-6 mt-10">
        <div className="w-full row flex">
          <div className="w-1/5 min-w-[130px] bg-[#f7f7f7] border-r border-[#d4d4d4]">&nbsp;</div>
          <div className="w-1/5 bg-[#f7f7f7] h-48 text-center font-bs flex items-center justify-center border-r border-[#d4d4d4]">
            {t('table.sph')}
          </div>
          <div className="w-1/5 bg-[#f7f7f7] h-48 text-center font-bs flex items-center justify-center border-r border-[#d4d4d4]">
            {t('table.cyl')}
          </div>
          <div
            className={`w-1/5 bg-[#f7f7f7] h-48 text-center font-bs flex items-center justify-center border-r border-[#d4d4d4]`}
          >
            {t('table.axis')}
          </div>
          <div className="w-1/5 bg-[#f7f7f7] h-48 text-center font-bs flex items-center justify-center">
            {t('table.add')}
          </div>
        </div>

        {/* OD 右眼 */}
        <div className="w-full row flex">
          <div className="w-1/5 min-w-[130px] bg-[#f7f7f7] border-r border-t border-[#d4d4d4] pl-10 flex items-center">
            <span className="leading-0 flex flex-col">
              <span className="text-14 font-bs">{t('table.od')}</span>
              <span className="text-12 font-br text-secondary-text mt-2">{t('table.odHint')}</span>
            </span>
          </div>
          <div
            className={`w-1/5  h-48 text-center font-br border-t flex items-center justify-center border-r border-[#d4d4d4] ${
              showError && sphDiff(prescription.prescription) ? errorTdClassName : ''
            }`}
          >
            {formatValue(prescription.prescription.od_sph, {
              showPlus: true,
              emptyText: NO_DATA,
            })}
          </div>
          <div
            className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r border-[#d4d4d4] ${
              showError && cylDiff(prescription.prescription) ? errorTdClassName : ''
            }`}
          >
            {formatValue(prescription.prescription.od_cyl, {
              showPlus: true,
              emptyText: NO_DATA,
            })}
          </div>
          <div
            className={`w-1/5  h-48 text-center font-br border-t flex items-center justify-center border-r border-[#d4d4d4] ${
              showGray && showGrayTd(prescription.prescription, 'od_axis') ? grayTdClassName : ''
            }`}
          >
            {prescription.prescription.od_axis || NO_DATA}
          </div>
          <div
            className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center ${
              showGray &&
              getADDData(prescription.prescription.od_add, prescription.prescription_type) ===
                NO_DATA
                ? grayTdClassName
                : ''
            }`}
          >
            {formatValue(
              getADDData(prescription.prescription.od_add, prescription.prescription_type),
              { showPlus: true },
            )}
          </div>
        </div>

        {/* OS 左眼 */}
        <div className="w-full row flex">
          <div className="w-1/5 min-w-[130px] bg-[#f7f7f7] border-r border-t border-[#d4d4d4] pl-10 flex items-center">
            <span className="leading-0 flex flex-col">
              <span className="text-14 font-bs">{t('table.os')}</span>
              <span className="text-12 font-br text-secondary-text mt-2">{t('table.osHint')}</span>
            </span>
          </div>
          <div
            className={`w-1/5  h-48 text-center font-br flex items-center justify-center border-r border-t border-[#d4d4d4] ${
              showError && sphDiff(prescription.prescription) ? errorTdClassName : ''
            }`}
          >
            {formatValue(prescription.prescription.os_sph, {
              showPlus: true,
              emptyText: NO_DATA,
            })}
          </div>
          <div
            className={`w-1/5 h-48 text-center font-br  flex items-center justify-center border-r border-t border-[#d4d4d4] ${
              showError && cylDiff(prescription.prescription) ? errorTdClassName : ''
            }`}
          >
            {formatValue(prescription.prescription.os_cyl, {
              showPlus: true,
              emptyText: NO_DATA,
            })}
          </div>
          <div
            className={`w-1/5  h-48 text-center font-br flex items-center justify-center border-r  border-t border-[#d4d4d4] ${
              showGray && showGrayTd(prescription.prescription, 'os_axis') ? grayTdClassName : ''
            }`}
          >
            {prescription.prescription.os_axis || NO_DATA}
          </div>
          <div
            className={`w-1/5 h-48 text-center font-br  flex items-center border-t border-[#d4d4d4] justify-center ${
              showGray &&
              getADDData(prescription.prescription.os_add, prescription.prescription_type) ===
                NO_DATA
                ? grayTdClassName
                : ''
            }`}
          >
            {formatValue(
              getADDData(prescription.prescription.os_add, prescription.prescription_type),
              { showPlus: true },
            )}
          </div>
        </div>

        {/* PD */}
        <div className="w-full row flex">
          <div className="w-1/5 min-w-[130px] bg-[#f7f7f7] border-r border-t border-[#d4d4d4] pl-10 flex items-center">
            <span className="leading-0 flex flex-col">
              <span className="text-14 font-bs">{t('table.pd')}</span>
              <span className="text-12 font-br text-secondary-text mt-2">{t('table.pdHint')}</span>
            </span>
          </div>
          {prescription.prescription.pd && (
            <div className="w-4/5  h-48 text-center font-br flex items-center justify-start pl-[33px] border-t border-[#d4d4d4]">
              {formatValue(prescription.prescription.pd)}
            </div>
          )}
          <div
            className={`w-4/5  h-48  font-br flex items-center justify-around border-t border-[#d4d4d4] ${
              showError && moreThan5Diff(prescription.prescription) ? errorTdClassName : ''
            }`}
          >
            {prescription.prescription.pd_l && prescription.prescription.pd_r ? (
              <>
                <span>
                  {formatValue(prescription.prescription.pd_r)}
                  <span
                    className={` ${
                      showError && moreThan5Diff(prescription.prescription)
                        ? errorTdClassName
                        : 'text-secondary-text'
                    }`}
                  >
                    {` ${t('table.odHint')}`}
                  </span>
                </span>
                <span>
                  {formatValue(prescription.prescription.pd_l)}
                  <span
                    className={` ${
                      showError && moreThan5Diff(prescription.prescription)
                        ? errorTdClassName
                        : 'text-secondary-text'
                    }`}
                  >
                    {` ${t('table.osHint')}`}
                  </span>
                </span>
              </>
            ) : (
              <span className="text-secondary-text" />
            )}
          </div>
        </div>
        {!isReadOnly && (
          <>
            {/* 上传处方 */}
            <div className="w-full row flex">
              <div className="w-full h-60 text-center font-br flex items-center justify-start pl-12 border-t border-[#d4d4d4]">
                {t('prescriptionImage')}{' '}
                <UploadPrescriptionImg
                  className="ml-10"
                  img={
                    prescription.prescriptions_image &&
                    env.NEXT_PUBLIC_S3_URL + prescription.prescriptions_image
                  }
                  onChange={onUploadImg}
                  allowDelete={false}
                >
                  <div className="h-48 f14 font-br flex items-center gap-4 text-primary-black cursor-pointer bg-[#f8f9fa] border border-dashed border-[#dce0e8] rounded-4 px-10 py-4">
                    <UploadImageOutLine className="text-16" />
                    {t('uploadImage')}
                  </div>
                </UploadPrescriptionImg>
              </div>
            </div>
          </>
        )}
      </div>
      {showPrism && hasPrism(prescription.prescription) && (
        <div
          className={`prescription-table border border-[#dce1e5] rounded-6 mt-10 ${tableClassName}`}
        >
          <div className="w-full row flex">
            <div className={`w-1/5 min-w-[130px] border-r`}>&nbsp;</div>
            <div
              className={`w-1/5 h-48 text-center text-14 font-bb flex items-center justify-center border-r`}
              dangerouslySetInnerHTML={{
                __html: t('table.prismHorizontal'),
              }}
            />
            <div
              className={`w-1/5 h-48 text-center text-14 font-bb flex items-center justify-center border-r`}
              dangerouslySetInnerHTML={{
                __html: t('table.baseDirection'),
              }}
            />
            <div
              className={`w-1/5 h-48 text-center text-14 font-bb flex items-center justify-center border-r`}
              dangerouslySetInnerHTML={{
                __html: t('table.prismVertical'),
              }}
            />
            <div
              className={`w-1/5 h-48 text-center text-14 font-bb flex items-center justify-center`}
              dangerouslySetInnerHTML={{
                __html: t('table.baseDirection'),
              }}
            />
          </div>

          {/* OD 右眼 */}
          <div className="w-full row flex">
            <div className={`w-1/5 min-w-[130px] border-r border-t pl-12`}>
              <span className="font-bs text-14 leading-0">
                <span>{t('table.od')}</span> <br />
                <span className="text-12 font-br text-secondary-text">{t('table.odHint')}</span>
              </span>
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'od_pv') ? grayTdClassName : ''
              }`}
            >
              {formatValue(prescription.prescription.od_pv || '0.00')}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'od_bd') ? grayTdClassName : ''
              }`}
            >
              {prescription.prescription.od_bd
                ? t(`table.${prescription.prescription.od_bd}` as any)
                : '—'}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center border-r ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'od_pv_r') ? grayTdClassName : ''
              }`}
            >
              {formatValue(prescription.prescription.od_pv_r || '0.00')}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br border-t flex items-center justify-center ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'od_bd_r') ? grayTdClassName : ''
              }`}
            >
              {prescription.prescription.od_bd_r
                ? t(`table.${prescription.prescription.od_bd_r}` as any)
                : '—'}
            </div>
          </div>

          {/* OS 左眼 */}
          <div className="w-full row flex">
            <div className={`w-1/5 min-w-[130px] border-r border-t pl-12`}>
              <span className="font-bs text-14 leading-0">
                <span>{t('table.os')}</span> <br />
                <span className="text-12 font-br text-secondary-text">{t('table.osHint')}</span>
              </span>
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br flex items-center justify-center border-r border-t ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'os_pv') ? grayTdClassName : ''
              }`}
            >
              {formatValue(prescription.prescription.os_pv || '0.00')}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br flex items-center justify-center border-r border-t ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'os_bd') ? grayTdClassName : ''
              }`}
            >
              {prescription.prescription.os_bd
                ? t(`table.${prescription.prescription.os_bd}` as any)
                : '—'}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br flex items-center justify-center border-r border-t ${innerTdClassName} ${
                showGray && showGrayTd(prescription.prescription, 'os_pv_r') ? grayTdClassName : ''
              }`}
            >
              {formatValue(prescription.prescription.os_pv_r || t('table.none'))}
            </div>
            <div
              className={`w-1/5 h-48 text-center font-br flex items-center border-t ${innerTdClassName} justify-center rounded-br-[6px] ${
                showGray && showGrayTd(prescription.prescription, 'os_bd_r') ? grayTdClassName : ''
              }`}
            >
              {prescription.prescription.os_bd_r
                ? t(`table.${prescription.prescription.os_bd_r}` as any)
                : '—'}
            </div>
          </div>
        </div>
      )}
      {!isReadOnly && (
        <div className="w-full flex justify-end mt-20 gap-24">
          <div className="flex items-center cursor-pointer gap-4" onClick={onDelete}>
            <DeleteOutlined className="!text-14 cursor-pointer" />
            <span className="f14 font-br  text-primary-black cursor-pointer">{t('delete')}</span>
          </div>
          <div className="flex items-center cursor-pointer gap-4" onClick={onEdit}>
            <EditOutlined className="!text-14 cursor-pointer" />
            <span className="f14 font-br  text-primary-black cursor-pointer">{t('edit')}</span>
          </div>
          {hasPrism(prescription.prescription) && (
            <div className="flex items-center cursor-pointer gap-4" onClick={onEdit}>
              <span className="f14 font-br  text-primary-black cursor-pointer">
                {t('viewPrism')}
              </span>
              <RightArrowOutlined className="text-14" />
            </div>
          )}
        </div>
      )}
    </article>
  );
}
